import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, Calendar } from "lucide-react";
import CalendlyEmbed from "../components/CalendlyEmbed";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { trackContactSubmit, trackLeadStart, trackAdsContactFormConversion } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  // Track when user starts filling the form
  const handleInputFocus = () => {
    if (!formStarted) {
      trackLeadStart('Contact Form');
      setFormStarted(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API}/contact?${params.toString()}`, {
        method: "POST",
      });

      if (!response.ok) throw new Error("Failed to submit");

      // Track successful submission
      trackContactSubmit();
      trackAdsContactFormConversion();
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-rnb-light to-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-rnb-teal" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
            Thank You!
          </h2>
          <p className="text-rnb-gray mb-6">
            We've received your message and will get back to you within 1-2 business days.
          </p>
          <Button onClick={() => setSubmitted(false)} variant="outline">
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Contact Us"
        description="Have questions about your clinic's operations, staffing, or compliance? Reach out to RNB Consulting Group. Call (888) 250-4605, email, or book a free consultation. No pressure, just real answers."
        keywords="healthcare consultant near me, contact medical practice consultant, schedule healthcare consultation, clinic help Atlanta, practice management support, healthcare operations help"
      />
      <StructuredData data={[
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://www.rnbconsultinggroup.com/contact" }
          ]
        },
        {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://www.rnbconsultinggroup.com/#business",
          "name": "RNB Consulting Group",
          "url": "https://www.rnbconsultinggroup.com/contact",
          "telephone": "(888) 250-4605",
          "email": "info@rnbconsultinggroup.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Atlanta",
            "addressRegion": "GA",
            "addressCountry": "US"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "17:00"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "(888) 250-4605",
            "contactType": "customer service",
            "email": "info@rnbconsultinggroup.com",
            "availableLanguage": "English"
          }
        }
      ]} />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-rnb-navy mb-6">
              Let&apos;s Talk About Your Practice
            </h1>
            <p className="text-lg text-rnb-gray mb-8">
              Have questions? Ready to get started? Schedule a free chat or drop us a message. 
              No pressure just a friendly conversation about how we might help.
            </p>
            <Link to="/consultation">
              <Button size="lg" className="btn-hover-lift">
                <Calendar className="mr-2 h-4 w-4" />
                Schedule Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-6">
                  Get in Touch
                </h2>
                <p className="text-rnb-gray mb-8">
                  Have questions? We're here to help. Reach out through any of these channels 
                  or fill out the form.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-rnb-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium text-rnb-navy mb-1">Email</h3>
                    <a
                      href="mailto:info@rnbconsultinggroup.com"
                      className="text-rnb-gray hover:text-rnb-blue transition-colors"
                    >
                      info@rnbconsultinggroup.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-rnb-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium text-rnb-navy mb-1">Phone</h3>
                    <a href="tel:888-250-4605" className="text-rnb-gray hover:text-rnb-blue transition-colors">
                      (888) 250-4605
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-rnb-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium text-rnb-navy mb-1">Location</h3>
                    <p className="text-rnb-gray">
                      Atlanta, GA, USA
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-rnb-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium text-rnb-navy mb-1">Business Hours</h3>
                    <p className="text-rnb-gray">
                      Monday - Friday<br />
                      9:00 AM - 5:00 PM EST
                    </p>
                    <p className="text-sm text-rnb-gray/70 mt-1">
                      Closed Saturday & Sunday
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2 border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-6">
                  Send Us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        onFocus={handleInputFocus}
                        placeholder="John Smith"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        onFocus={handleInputFocus}
                        placeholder="john@company.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company/Organization</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleChange("company", e.target.value)}
                        onFocus={handleInputFocus}
                        placeholder="Acme Healthcare"
                        data-testid="contact-company"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">Service Interest</Label>
                    <Select
                      value={formData.service_interest}
                      onValueChange={(value) => handleChange("service_interest", value)}
                    >
                      <SelectTrigger data-testid="contact-service">
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="workforce">Workforce Optimization</SelectItem>
                        <SelectItem value="operations">Operations Excellence</SelectItem>
                        <SelectItem value="compliance">Compliance & Risk</SelectItem>
                        <SelectItem value="patient-experience">Patient Experience</SelectItem>
                        <SelectItem value="emr">EMR Implementation</SelectItem>
                        <SelectItem value="growth">Growth Strategy</SelectItem>
                        <SelectItem value="carerevive">CareRevive Program</SelectItem>
                        <SelectItem value="foundations">Foundations First Program</SelectItem>
                        <SelectItem value="frontline">Frontline Concierge Program</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Tell us about your organization and how we can help..."
                      rows={5}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full btn-hover-lift"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Calendly Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
              Schedule a Free Consultation
            </h2>
            <p className="text-rnb-gray max-w-2xl mx-auto">
              Book a 30-minute call with our team to discuss your healthcare practice goals 
              and learn how we can help.
            </p>
          </div>
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-0">
              <CalendlyEmbed 
                url="https://calendly.com/rnb-consulting-group/free-discovery-call"
                height="700px"
              />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
